import React, { memo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileCopyIcon from '@mui/icons-material/FileCopyOutlined';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { ConfirmActionDialog } from '../uiComponents';
import colors from './colors';
import { questionTypeMask } from './helper';
import { iconTypes } from './iconTypes';
import { InfoQuestion } from './infoQuestion';
import { questionTypesList } from './questionTypes';
import { selectorTypes } from './selectorTypes';

interface StyleSheet {
  [key: string]: React.CSSProperties;
}

const TableQuestionMenu = memo(
  (props: {
    tableId: string;
    index: number;
    question: any;
    onAddInTableHandler: any;
    onDeleteInTableHandler: any;
    onDuplicateInTableHandler: any;
    onTypeInTableChange: any;
    disableDelete: boolean;
    availableFileUpload: boolean;
  }) => {
    const {
      tableId,
      index,
      question,
      onAddInTableHandler,
      onDeleteInTableHandler,
      onDuplicateInTableHandler,
      onTypeInTableChange,
      disableDelete,
      availableFileUpload,
    } = props;

    const styles: StyleSheet = {
      iconContainer: {
        cursor: 'pointer',
        paddingTop: 3,
        marginLeft: 14,
      },
      icon: {
        fontSize: 18,
      },
    };
    const [openedAlert, setOpenedAlert] = useState(false);
    const { checkFlag } = useFeatureFlags();

    const lang = useLanguage() as any;

    const deleteHandler = () => {
      setOpenedAlert(false);
      onDeleteInTableHandler(question.id, tableId);
    };

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: '0 3px 0',
        }}
      >
        <div>
          <Select
            MenuProps={{ autoFocus: false }}
            disableUnderline
            variant='standard'
            value={questionTypeMask(question.type)}
            onChange={(e: any) => onTypeInTableChange(question.id, e.target.value, tableId)}
            renderValue={(typeValue: any) => {
              const value = questionTypesList.includes(typeValue) ? typeValue : 'deprecated';
              const icon = iconTypes(15, 5, colors.primary)[value];

              return (
                <Tooltip title={lang.form_builder.questionType}>
                  <div
                    className='formBuilderTypeQuestionMenuSelect'
                    style={{
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {icon}
                    {lang.types[value]}
                  </div>
                </Tooltip>
              );
            }}
          >
            {selectorTypes
              .filter(
                (type) =>
                  type.value !== 'table' &&
                  (availableFileUpload ? true : type.value !== 'attachment'),
              )
              .filter(
                (type) =>
                  type.value !== 'select_activity_period_time' || checkFlag('ActivityPeriodTime'),
              )
              .map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                >
                  <InfoQuestion
                    type={type.value}
                    source={type.pic}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      {iconTypes(15, 5, colors.primary)[type.value]}
                      {lang.types[type.value]}
                    </div>
                  </InfoQuestion>
                </MenuItem>
              ))}
          </Select>
        </div>
        <div style={{ display: 'flex', flex: 1 }} />
        <div
          style={styles.iconContainer}
          onClick={() => onDuplicateInTableHandler(question.id, index, tableId)}
        >
          <Tooltip title={lang.form_builder.copy}>
            <FileCopyIcon style={{ fontSize: 16 }} />
          </Tooltip>
        </div>
        <div
          style={styles.iconContainer}
          onClick={() => {
            if (!disableDelete) {
              setOpenedAlert(true);
            }
          }}
        >
          <Tooltip title={lang.form_builder.delete}>
            <DeleteOutlineIcon
              style={styles.icon}
              color={disableDelete ? 'disabled' : 'inherit'}
            />
          </Tooltip>
        </div>

        <div
          style={{
            cursor: 'pointer',
            marginLeft: 14,
            backgroundColor: colors.primary,
            borderRadius: 100,
            height: 18,
            width: 18,
          }}
          onClick={() => onAddInTableHandler(index, tableId)}
        >
          <Tooltip title={lang.form_builder.add}>
            <AddIcon style={{ ...styles.icon, color: 'white', marginBottom: '9px' }} />
          </Tooltip>
        </div>

        {openedAlert && (
          <ConfirmActionDialog
            isOpen={openedAlert}
            onClose={() => setOpenedAlert(false)}
            warningTitle={lang.form_builder.areYouSure}
            warningMessage={lang.form_builder.questionAlert}
            onConfirmAction={deleteHandler}
            confirmActionButtonText={lang.form_builder.yesDelete}
          />
        )}
      </div>
    );
  },
);

export { TableQuestionMenu };
